import { useEffect, useMemo, useState } from "react";
import GameShell from "../GameShell";

/**
 * Star Path — Trail Making Test (TMT-B style).
 * Show 8 randomized "stars" with labels 1,A,2,B,3,C,4,D.
 * Child must click them in correct alternating order: 1 → A → 2 → B → 3 → C → 4 → D.
 */
export default function StarPath() {
  return (
    <GameShell gameId="star_path" title="Caminho das Estrelas">
      {({ onFinish }) => <StarGame onFinish={onFinish} />}
    </GameShell>
  );
}

const SEQUENCE = ["1", "A", "2", "B", "3", "C", "4", "D"];

function randPositions(n, w, h) {
  // Generate n positions with min distance
  const out = [];
  let tries = 0;
  while (out.length < n && tries < 500) {
    tries++;
    const x = 60 + Math.random() * (w - 120);
    const y = 60 + Math.random() * (h - 120);
    const ok = out.every((p) => Math.hypot(p.x - x, p.y - y) > 90);
    if (ok) out.push({ x, y });
  }
  return out;
}

function StarGame({ onFinish }) {
  const [w, h] = [820, 480];
  const stars = useMemo(() => {
    const pos = randPositions(SEQUENCE.length, w, h);
    return SEQUENCE.map((label, i) => ({ id: i, label, ...pos[i] }));
  }, []);
  const [stepIdx, setStepIdx] = useState(0); // next to click
  const [errors, setErrors] = useState(0);
  const [startedAt] = useState(() => performance.now());
  const [reactionTimes, setReactionTimes] = useState([]);
  const [lastClickAt, setLastClickAt] = useState(() => performance.now());
  const [path, setPath] = useState([]); // ids in order clicked correctly

  const expected = SEQUENCE[stepIdx];

  const click = (s) => {
    const now = performance.now();
    if (s.label === expected) {
      setReactionTimes((rs) => [...rs, now - lastClickAt]);
      setLastClickAt(now);
      const newPath = [...path, s.id];
      setPath(newPath);
      const next = stepIdx + 1;
      if (next >= SEQUENCE.length) {
        const elapsed = now - startedAt;
        const score = Math.max(0, Math.round(200 - errors * 10 - elapsed / 200));
        onFinish({
          score,
          duration_ms: Math.round(elapsed),
          metrics: {
            errors,
            hits: SEQUENCE.length,
            avg_reaction_ms: Math.round(
              reactionTimes.concat(now - lastClickAt).reduce((a, b) => a + b, 0) /
                (reactionTimes.length + 1)
            ),
          },
        });
      } else {
        setStepIdx(next);
      }
    } else {
      setErrors((e) => e + 1);
    }
  };

  return (
    <div className="mt-4 max-w-4xl mx-auto" data-testid="star-area">
      <div className="text-center text-white drop-shadow mb-4">
        <div className="text-sm">Ligue alternando: número, letra, número, letra…</div>
        <div className="text-lg font-bold mt-1">
          Próximo: <span className="bg-white/95 text-slate-900 px-3 py-1 rounded-full">{expected}</span>
          <span className="ml-3 text-sm opacity-80">Erros: {errors}</span>
        </div>
      </div>

      <div className="kid-card relative" style={{ height: h + 40, paddingTop: 20 }}>
        <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block" }}>
          {/* path lines */}
          {path.length > 1 &&
            path.slice(0, -1).map((id, i) => {
              const a = stars[id];
              const b = stars[path[i + 1]];
              return (
                <line
                  key={i}
                  x1={a.x}
                  y1={a.y}
                  x2={b.x}
                  y2={b.y}
                  stroke="#F472B6"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
              );
            })}
          {stars.map((s) => {
            const done = path.includes(s.id);
            const isNext = s.label === expected;
            return (
              <g
                key={s.id}
                onClick={() => click(s)}
                style={{ cursor: "pointer" }}
                data-testid={`star-${s.label}`}
              >
                <circle
                  cx={s.x}
                  cy={s.y}
                  r={32}
                  fill={done ? "#A3E635" : isNext ? "#FBBF24" : "white"}
                  stroke={done ? "#65A30D" : isNext ? "#D97706" : "#94A3B8"}
                  strokeWidth="3"
                />
                <text
                  x={s.x}
                  y={s.y + 8}
                  textAnchor="middle"
                  fontSize="22"
                  fontWeight="700"
                  fill={done ? "white" : "#1E293B"}
                  fontFamily="Fredoka, sans-serif"
                >
                  {s.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
